import { fetchCurrentAllocations } from 'views/Portfolio/components/Allocation/services/fetchCurrentAllocations';
import { ToolExecutionResult } from '../types';
import { formatCurrency, formatPercentage } from '../utils/formatters';

export const executeGetRegionalAllocation = async (): Promise<ToolExecutionResult> => {
  try {
    const response = await fetchCurrentAllocations();

    if (!response.portfolioCurrentAllocation || response.portfolioCurrentAllocation.length === 0) {
      return {
        success: true,
        data: {
          message: 'No allocation data available',
          allocations: [],
        },
      };
    }

    // Combine allocation data with performance data
    const regionPerformanceMap = new Map(response.regionPerformances?.map((rp) => [rp.regionName, rp]) || []);

    const allocations = response.portfolioCurrentAllocation.map((alloc) => {
      const perf = regionPerformanceMap.get(alloc.regionName);

      return {
        region: alloc.regionName,
        currentAllocation: formatPercentage(alloc.currentAllocation * 100),
        tacticalAllocation: formatPercentage(alloc.tacticalAllocation * 100),
        strategicAllocation: formatPercentage(alloc.strategicAllocation * 100),
        currentHoldings: perf ? formatCurrency(perf.currentHoldings) : 'N/A',
        totalPurchasePrice: perf ? formatCurrency(perf.totalPurchasePrice) : 'N/A',
        profitAndLoss: perf ? formatCurrency(perf.profitAndLoss) : 'N/A',
        profitAndLossPct: perf ? formatPercentage(perf.profitAndLossPct) : 'N/A',
      };
    });

    // Sort by current allocation descending
    allocations.sort((a, b) => {
      const aVal = parseFloat(a.currentAllocation.replace('%', ''));
      const bVal = parseFloat(b.currentAllocation.replace('%', ''));
      return bVal - aVal;
    });

    // Calculate totals from region performances
    const totalHoldings = response.regionPerformances?.reduce((sum, rp) => sum + rp.currentHoldings, 0) || 0;
    const totalPurchasePrice = response.regionPerformances?.reduce((sum, rp) => sum + rp.totalPurchasePrice, 0) || 0;
    const totalPnL = response.regionPerformances?.reduce((sum, rp) => sum + rp.profitAndLoss, 0) || 0;

    return {
      success: true,
      data: {
        allocations,
        totals: {
          totalHoldings: formatCurrency(totalHoldings),
          totalPurchasePrice: formatCurrency(totalPurchasePrice),
          totalProfitAndLoss: formatCurrency(totalPnL),
          totalProfitAndLossPct:
            totalPurchasePrice > 0 ? formatPercentage((totalPnL / totalPurchasePrice) * 100) : '0%',
        },
        regionCount: allocations.length,
      },
    };
  } catch (error) {
    return {
      success: false,
      error: `Failed to fetch regional allocation: ${(error as Error).message}`,
    };
  }
};
